import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setFieldPath } from "./wizardSetField";

// Session A2 — the Sponsored Display create-wizard slice.
//
// RULE (v2 plan §2): the existing `addCampaign` slice is NOT touched. This one
// holds ONE campaign and ONE ad group, shaped exactly like the §5 payload
// contract for `POST /amazon/sd/campaign/create`, so the review step and the
// launch call read the same object instead of re-deriving it.
//
// Fields marked "UI only" below are stripped by B7's adapter before the post.

export type SdCostType = "cpc" | "vcpm";
export type SdBidOptimization = "reach" | "clicks" | "conversions";
export type SdAdFormat = "IMAGE" | "VIDEO";

export interface SdProduct {
  asin: string;
  sku?: string | null;
  title?: string; // UI only
  imageUrl?: string; // UI only
  price?: number | null; // UI only
}

export interface SdContextualTarget {
  id: string; // UI only
  family: "CONTEXTUAL";
  kind: "product" | "category";
  value: string;
  label?: string; // UI only
  refinements: {
    /** Amazon brand ids — what `asinBrandSameAs` takes. */
    brands: string[];
    priceRange: { min: number | null; max: number | null };
    rating: { min: number | null; max: number | null };
  };
  /**
   * UI only (Session B5) — brandId → display name, so the added-targets panel
   * can render a brand chip without a second lookup. A map rather than a
   * parallel array precisely so it cannot fall out of step with
   * `refinements.brands`: an id with no entry falls back to printing the id.
   */
  brandLabels?: Record<string, string>;
  bid: number | null;
  suggestedBid?: number | null; // UI only (G4; "—" when the call fails)
}

export interface SdRemarketingTarget {
  id: string; // UI only
  family: "REMARKETING";
  audienceType: "views" | "purchases";
  productScope: string;
  lookback: number;
  label?: string; // UI only
  bid: number | null;
  suggestedBid?: number | null; // UI only
}

export interface SdAmazonAudienceTarget {
  id: string; // UI only
  family: "AMAZON_AUDIENCE";
  audienceId: string;
  label?: string; // UI only
  bid: number | null;
  suggestedBid?: number | null; // UI only
}

export type SdTarget =
  | SdContextualTarget
  | SdRemarketingTarget
  | SdAmazonAudienceTarget;

/**
 * Session B8 — a crop box in the ASSET'S OWN pixels, which is the space
 * Amazon's `croppingCoordinates` uses. Never display pixels: the editor renders
 * the logo at whatever width the pane happens to be, and a box measured there
 * would mean something different on a different screen.
 */
export interface SdCropBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Session B8 — one Creative Asset Library entry.
 *
 * `width` / `height` are NOT UI-only despite looking like it: the backend needs
 * them to build the `croppingCoordinates` Amazon carries on every live creative
 * (measured — even an uncropped logo has an explicit box). `url` and `name` are
 * the UI-only pair, stripped by the launch adapter.
 */
export interface SdCreativeAsset {
  assetId: string;
  assetVersion: string;
  width: number | null;
  height: number | null;
  /**
   * The box the logo editor produced. Also not UI-only, and it is the reason
   * the editor exists: `sdCropBox` in AmazonHelper used to centre every crop
   * with a comment saying "the console lets a human drag the crop, we have no
   * such control". This is that control, so an explicit box now overrides the
   * centred default. Null / absent still means "the whole asset".
   */
  crop?: SdCropBox | null;
  /**
   * A VIDEO asset only, and never set by an upload in this wizard — Amazon
   * derives it and returns it on the stored creative. It exists so a creative
   * READ BACK off Amazon (the Ad Group Details Creative tab) can be previewed
   * with the exact pair Amazon holds instead of the `assetId` fallback
   * `sdCreativeProperties` applies when it is absent.
   */
  originalAssetId?: string | null;
  originalAssetVersion?: string | null;
  url?: string | null; // UI only
  name?: string | null; // UI only
  /** The file's size in bytes, printed by the Edit image modal. UI only. */
  bytes?: number | null; // UI only
  /**
   * Session D3 — a video's length in seconds, measured in the browser before
   * the upload (Amazon's own register response carries no duration back).
   * UI only: it is what the Video panel prints and what the 6–45s check ran
   * against, and Amazon re-derives it from the file itself.
   */
  durationSeconds?: number | null; // UI only
}

/**
 * Session B12 — Amazon's three Sponsored Display placement crops.
 *
 * Its console shows ONE "Image" control but stores three: `squareImages`,
 * `horizontalImages` and `verticalImages`, each a differently-shaped crop of the
 * source (measured on 30 live creatives — see `sdImageCrops` in AmazonHelper).
 * Until B12 the wizard collected one image and let the backend centre-crop all
 * three; Amazon's console lists them as three editable rows, each with its own
 * Edit and its own **Replace**, and this is that list.
 */
export type SdImageAspect = "horizontal" | "square" | "vertical";

/**
 * One placement crop. It carries its own asset because Amazon's "Replace" is
 * per row: a customer can serve a different photo to the vertical slot than to
 * the square one, and the API models exactly that (three independent keys, one
 * entry each — the shape B8 read back live).
 */
export interface SdImageCrop extends SdCreativeAsset {
  /**
   * Where the customer dragged the focal square, as a fraction of the asset.
   * UI only — `crop` is what Amazon is sent; this is what regenerates it when
   * the region or the asset changes, and what re-opens the editor where it was
   * left.
   */
  focal?: { x: number; y: number } | null;
  /**
   * The area of the source the crop is fitted inside — the "Crop image"
   * disclosure's box, absent when the customer never opened it. UI only, for
   * the same reason as `focal`.
   */
  region?: SdCropBox | null;
}

export type SdImageCrops = Record<SdImageAspect, SdImageCrop | null>;

export const emptyImageCrops = (): SdImageCrops => ({
  horizontal: null,
  square: null,
  vertical: null,
});

/**
 * Session B8 — Amazon's Creative section. Every part is optional and
 * independent: an SD ad is valid with none of them (Amazon generates the
 * creative from the products), which is exactly what the wizard did before B8
 * and what it still does when this step is skipped.
 *
 * The field names below are OURS; the Amazon ones (`brandLogo`, `headline`,
 * `squareImages`/`horizontalImages`/`verticalImages`) are applied by the
 * backend, which is also where the one-image-to-three-crops fan-out lives.
 */
export interface SdCreative {
  logo: SdCreativeAsset | null;
  headline: string;
  /**
   * The image the customer added — "the original", as the Edit image modal
   * labels its dimensions. It is what the Image row summarises and what every
   * crop starts as a copy of; `imageCrops` is what Amazon is actually sent.
   */
  image: SdCreativeAsset | null;
  /** Session B12 — the three placement crops. Empty when no image is set. */
  imageCrops: SdImageCrops;
  /**
   * Session D3 — the video, and the one part of this object that is NOT
   * optional in every case: an ad group whose format is VIDEO must carry one
   * (the SD 3.0 spec's `CreativeTypeInCreativeRequest`: *"This type of creative
   * must have video assets provided"*), which is why the Creative step gates on
   * it while gating on nothing else.
   *
   * It never coexists with `image` in a payload. The wizard clears whichever
   * side the format is not, and both the launch adapter and
   * `sdCreativeProperties` drop the other half again — a video ad has no Image
   * row in Amazon's console and no image keys on its creative.
   */
  video: SdCreativeAsset | null;
}

/**
 * Session B8 — which of the three Creative switches the user has ON.
 *
 * UI ONLY, and it lives OUTSIDE `creative` on purpose: the launch adapter builds
 * its payload field by field out of `creative`, so an extra key in there would
 * be silently harmless today and silently sent the day someone spreads the
 * object. Here it cannot be.
 *
 * It is state rather than `Boolean(creative.logo)` because the two differ in
 * both directions: Amazon's card opens with **Logo on and empty** (that is its
 * default, and the empty row nags rather than blocks), and a row switched off
 * must stay off after the user walks to step 7 and back.
 */
export interface SdCreativeRows {
  logo: boolean;
  headline: boolean;
  image: boolean;
}

export interface SdCampaignState {
  currentStep: number;
  profileId: string | null;
  portfolioId: string | null;
  /**
   * UI-only, like `creativeRows` — never sent, `buildSdLaunchPayload` names its
   * keys one by one. Written by Step 1 once the Settings card's portfolio fetch
   * resolves, because whether the requirement is enforceable is something only
   * that fetch knows (an account with no writable portfolio cannot satisfy it).
   * It lives here so the Review step's blocker list — a pure read of the slice
   * — can name the missing portfolio instead of leaving Launch dead and silent.
   */
  portfolioRequired: boolean;
  campaign: {
    name: string;
    budget: number | null;
    startDate: string; // YYYY-MM-DD
    endDate: string | null;
    costType: SdCostType;
    bidOptimization: SdBidOptimization;
    costControl: { enabled: boolean; value: number | null };
  };
  adGroup: {
    name: string;
    adFormat: SdAdFormat;
    defaultBid: number | null;
  };
  products: SdProduct[];
  targets: SdTarget[];
  creative: SdCreative;
  creativeRows: SdCreativeRows;
}

// Name / start date are prefilled by A3's CampaignSettings on mount, not here —
// a module-level `new Date()` goes stale in a long-lived tab.
const initialState: SdCampaignState = {
  currentStep: 1,
  profileId: null,
  portfolioId: null,
  // False until the Settings card's fetch says otherwise — an unresolved list
  // must never block a launch on a portfolio nobody has been offered yet.
  portfolioRequired: false,
  campaign: {
    name: "",
    budget: null,
    startDate: "",
    endDate: null,
    costType: "cpc",
    bidOptimization: "clicks",
    costControl: { enabled: false, value: null },
  },
  adGroup: {
    name: "",
    adFormat: "IMAGE",
    defaultBid: null,
  },
  products: [],
  targets: [],
  // Session B8. All three empty, which is what "the user skipped the Creative
  // step" looks like — and what makes the backend send no creative at all.
  creative: {
    logo: null,
    headline: "",
    image: null,
    imageCrops: emptyImageCrops(),
    video: null,
  },
  // Amazon's own defaults, read off its card: Logo on, Headline and Image off.
  // Logo-on-and-empty is not a bug — it is Amazon nudging you to brand the ad,
  // and it never stops a launch here (see SDStep6Creative's validity note).
  creativeRows: {
    logo: false,
    headline: false,
    image: false,
  },
};

const createSdCampaignSlice = createSlice({
  name: "createSdCampaign",
  initialState,
  reducers: {
    setField: (
      state,
      action: PayloadAction<{ path: string; value: unknown }>
    ) => {
      setFieldPath(state, action.payload.path, action.payload.value);
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    reset: () => initialState,
  },
});

export const { setField, setStep, reset } = createSdCampaignSlice.actions;
export const sdInitialState = initialState;
export default createSdCampaignSlice.reducer;
